import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProduct, useProducts } from '../hooks/useProducts';
import type { ProductInput } from '../types';
import {
  fieldLabelStyle,
  formFieldStyle as fieldStyle,
  formNumberFieldStyle as numberFieldStyle,
  formNumberRowStyle as numberRowStyle,
  formSectionStyle as sectionStyle,
  formSectionTitleStyle as sectionTitleStyle,
  formSubmitStyle,
} from '../lib/theme';

const EMPTY: ProductInput = {
  name: '',
  caloriesPer100g: 0,
  proteinPer100g: null,
  fatPer100g: null,
  carbsPer100g: null,
  category: '',
};

export default function ProductForm() {
  const { id } = useParams();
  const productId = id ? Number(id) : undefined;
  const { product } = useProduct(productId);
  const { createProduct, updateProduct } = useProducts();
  const navigate = useNavigate();

  const [form, setForm] = useState<ProductInput>(EMPTY);

  useEffect(() => {
    if (product) setForm(product);
  }, [product]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (productId) await updateProduct(productId, form);
    else await createProduct(form);
    navigate('/products');
  }

  return (
    <div>
      <h2>{productId ? 'Edit product' : 'Add product'}</h2>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: 520 }}>
        <section style={sectionStyle}>
          <label style={fieldLabelStyle}>
            Name
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              style={fieldStyle}
            />
          </label>

          <label style={fieldLabelStyle}>
            Category
            <input
              value={form.category ?? ''}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="e.g. Meat, Dairy, Vegetables"
              style={fieldStyle}
            />
          </label>
        </section>

        <section style={sectionStyle}>
          <div style={sectionTitleStyle}>Nutrition per 100g</div>
          <label style={fieldLabelStyle}>
            Calories (kcal)
            <input
              type="number"
              value={form.caloriesPer100g === 0 ? '' : form.caloriesPer100g}
              onChange={(e) =>
                setForm({ ...form, caloriesPer100g: e.target.value === '' ? 0 : Number(e.target.value) })
              }
              required
              style={fieldStyle}
            />
          </label>

          <div style={numberRowStyle}>
            <label style={numberFieldStyle}>
              Protein (g)
              <input
                type="number"
                value={form.proteinPer100g ?? ''}
                onChange={(e) => setForm({ ...form, proteinPer100g: e.target.value ? Number(e.target.value) : null })}
                style={fieldStyle}
              />
            </label>
            <label style={numberFieldStyle}>
              Fat (g)
              <input
                type="number"
                value={form.fatPer100g ?? ''}
                onChange={(e) => setForm({ ...form, fatPer100g: e.target.value ? Number(e.target.value) : null })}
                style={fieldStyle}
              />
            </label>
            <label style={numberFieldStyle}>
              Carbs (g)
              <input
                type="number"
                value={form.carbsPer100g ?? ''}
                onChange={(e) => setForm({ ...form, carbsPer100g: e.target.value ? Number(e.target.value) : null })}
                style={fieldStyle}
              />
            </label>
          </div>
        </section>

        <button type="submit" style={formSubmitStyle}>
          {productId ? 'Save changes' : 'Add product'}
        </button>
      </form>
    </div>
  );
}
